import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { SplitButtonService } from '../../services/split-button.service';
import { Subject } from 'rxjs';
import { StringUtils } from '../../utils/String.utils';
import { filter, takeUntil } from 'rxjs/operators';
import { BlurItemService } from '@ispm/shared/services/blur-item.service';
import { Widget } from '@ispm/widgets/decorator/widget.decorator';
import { IWidgetItem } from '@ispm/widgets/interfaces/widget-item.interface';
import { IdynamicFormItem, IDynamicFormItemTooltipPattern, IDynamicFormItemTooltipWidget } from '@ispm/dynamic-form/interfaces/IdynamicFormItem';
import { get } from 'lodash';
import { IEService } from '@ispm/shared/services/IE.service';
import { tooltipPositions } from '@ispm/shared/directives/tooltips/tooltip.directive';

@Component({
  selector: 'gen-button',
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@Widget('Button')
export class ButtonComponent implements OnInit, OnDestroy, IWidgetItem {

  @Input()
  public dataPanelPosition: 'TOP' | 'BOTTOM' = 'BOTTOM';

  @Input()
  set type(type: keyof IButtonTypes) {
    this.btnType = (type && type.toLowerCase()) as keyof IButtonTypes || buttonTypes.primary;
    this.btnClasses = '';
    switch (this.btnType) {
      case buttonTypes.emphasis:
        this.btnClasses += ' emphasis__button';
      // should get the 'quite' css
      case buttonTypes.quite:
        this.btnClasses += ' quiet__button';
        break;
      case buttonTypes.secondary:
        this.btnClasses += ' secondary__button';
        break;
      case buttonTypes.border:
        this.btnClasses += ' border__button';
        break;
      case buttonTypes.split:
        this.btnClasses += ' split__button';
        break;
      case buttonTypes.proceed:
        this.btnClasses += ' primary__button proceed-button';
        break;
      case buttonTypes.accent:
        this.btnClasses += ' accent__button';
        break;
      case buttonTypes.success:
        this.btnClasses += ' success__single__button';
        break;
      case buttonTypes.warning:
        this.btnClasses += ' warning__single__button';
        break;
      case buttonTypes.error:
        this.btnClasses += ' error__single__button';
        break;
      case buttonTypes.quiteDefault:
        this.btnClasses += ' quiet__default__button';
      case buttonTypes.primary:
      default:
        this.btnClasses += ' primary__button';
        this.btnType = 'primary';
        break;
    }
    this.initType();
  }

  @Output() dropdownVisibilityChanged: EventEmitter<boolean> = new EventEmitter();

  get type() {
    return this.btnType;
  }

  @Input() text: string = '';
  @Input() iconClass: string = '';
  @Input() dropDownIconClass: string = 'icon__dropdown-arrow'; // todo: Check with Guy and Amnon why this is the default icon class
  @Input() submit: boolean = false;
  @Input() fullSize: boolean = false;
  @Input() minPadding: boolean = false;
  /**
   * For setting applying :active style for long terms
   *
   * common use is for emphasis-quite style
   * @link http://html5-toolkit.ta.philips.com/dls/#/angular/components/button/examples
   * @type {boolean}
   */
  @Input() active: boolean = false;
  @Input() disabled: boolean = false;
  @Input() permissionKey: string = '';
  @Input() permissionAction: string = 'hide';
  @Input() round: boolean = false;
  @Input() btnClasses: string = '';
  @Input() textClass: string = '';
  @Input() tag: string = '';
  @Input()
  set tooltip(text: string) {
    this.tooltipText = text || '';
    this.tooltipShow = !!text.trim();
  }

  private btnType: keyof IButtonTypes;
  public showDropdownContent: boolean = false;
  @ViewChild('detailsSection', { static: false }) detailsSectionRef: ElementRef;
  @ViewChild('mainButton', { static: false }) defaultButtonRef: ElementRef;

  private widgetConfigPayload; any;
  public position: string = '';
  public tooltipPosition = tooltipPositions.BOTTOM;
  public tooltipShow: boolean = false;
  public tooltipText: string = '';
  @Input() buttonClass: string = '';
  public tooltipConfiguration ?: IDynamicFormItemTooltipPattern | IDynamicFormItemTooltipWidget;

  widgetUpdateValueCallback: (value) => void;

  private destroyer: Subject<null> = new Subject<null>();
  private id: string;

  set widgetConfig(widgetConfig: IdynamicFormItem) {
    Object.assign(this, get(widgetConfig, 'widgetPayload', { }));
    this.widgetConfigPayload = get(widgetConfig, 'widgetPayload', { });
    this.tooltipShow = get(widgetConfig, 'tooltip.show', false);
    this.tooltipText = get(widgetConfig, 'tooltip.text', '');
    this.tooltipPosition = get(widgetConfig, 'tooltip.position', tooltipPositions.BOTTOM);
    this.text = get(widgetConfig, 'text', '');
    this.textClass = get(widgetConfig, 'textClass', '');
    this.position = get(this.widgetConfigPayload, 'position', '');
    this.buttonClass = get(this.widgetConfigPayload, 'buttonClass', '');
    this.permissionKey = get(widgetConfig, 'permissionKey', false);
    this.tooltipConfiguration = get(this.widgetConfigPayload, 'tooltipConfiguration');
  }

  set widgetData(widgetData: any) {
  }

  constructor(
    private splitButtonService: SplitButtonService,
    private _zone: NgZone,
    private ieService: IEService
    ) {
  }

  ngOnInit() {
    if (!this.type) {
      this.type = buttonTypes.primary;
    }
    else {
      this.initType();
    }
  }

  initType() {
    this.destroyer.next();
    switch (this.type) {
      case buttonTypes.split:
        this.id = StringUtils.generateGuid();
        this.splitButtonService.subscriber.pipe(
          takeUntil(this.destroyer.asObservable()),
          filter(activeButtonId => activeButtonId !== this.id)
        ).subscribe(() => {
          this.hideDropdown();
        });
        break;
      default:
        break;
    }
  }

  ngOnDestroy() {
    this.destroyer.next();
    this.destroyer.complete();
  }
// Since all the pointers events controlled by css may  not host binding
/*  @HostBinding('style.pointerEvents')
  get pointerStyle() {
    return this.disabled ? 'none' : 'inherit';
  }*/

  @HostListener('click', ['$event'])
  click(event: MouseEvent) {
    // Prevent event emit
    event.stopPropagation();

    BlurItemService.triggerBlurItems();
    // Emit event only after blur completed for all items
    this._zone.runOutsideAngular(() => {
      setTimeout(() => {
        let ev;
        if (this.ieService.isIE()) {
          ev = new CustomEvent('click', { bubbles: true, cancelable: true });
        } else {
          ev = new Event('click', { bubbles: true, cancelable: true });
        }
        dispatchEvent(ev);
      }, 0);
    });
  }

  dropdownClick($event) {
    $event.stopPropagation();
    this.showDropdownContent = !this.showDropdownContent;
    if (this.showDropdownContent) {
      setTimeout(() => {
        this.locateDataPanel($event.target);
        this.dropdownVisibilityChanged.emit(true);
      }, 10);
      this.splitButtonService.setActiveButton(this.id);
    }
  }

  hideDropdown() {
    if (this.showDropdownContent) {
      this.showDropdownContent = false;
      this.dropdownVisibilityChanged.emit(false);
    }
  }

  updateEditableAllow(permissionNotExist: boolean) {
    this.disabled = this.disabled ? this.disabled : !permissionNotExist;
  }

  locateDataPanel(dropdownButton) {
    // const defaultButton = this.defaultButtonRef.nativeElement;
    // const totalButtonsWidth: number = defaultButton.offsetWidth + dropdownButton.offsetWidth;
    const detailsSection = this.detailsSectionRef.nativeElement;
    // const panelLeft: number = defaultButton.offsetLeft - (detailsSection.offsetWidth - totalButtonsWidth)/2;
    // panel located in the middle
    const dropdownButtonLeft = dropdownButton.offsetLeft;
    const dropdownButtonWidth = dropdownButton.offsetWidth;
    const detailsSectionWidth = detailsSection.offsetWidth;
    let panelLeft: number = dropdownButtonLeft + dropdownButtonWidth - detailsSectionWidth; // panel located on left of the buttons
    if (panelLeft < 0) {
      panelLeft = 0;
    } else if (panelLeft + detailsSection.offsetWidth > window.innerWidth) {
      panelLeft = window.innerWidth - detailsSection.offsetWidth;
    }
    detailsSection.style.left = panelLeft + 'px';

    // Sometimes element width is changed after presented. If it was changed, calculate left position again.
    if (detailsSectionWidth !== detailsSection.offsetWidth) {
      panelLeft = dropdownButtonLeft + dropdownButtonWidth - detailsSection.offsetWidth;
      if (panelLeft < 0) {
        panelLeft = 0;
      } else if (panelLeft + detailsSection.offsetWidth > window.innerWidth) {
        panelLeft = window.innerWidth - detailsSection.offsetWidth;
      }
      detailsSection.style.left = panelLeft + 'px';
    }
    switch (this.dataPanelPosition.toUpperCase()) {
      case 'BOTTOM':
        detailsSection.style.top = dropdownButton.getBoundingClientRect().bottom + 'px';
        break;
      case 'TOP':
        detailsSection.style.bottom = dropdownButton.getBoundingClientRect().height + 7 + 'px';
        break;
    }
    detailsSection.style.opacity = 1;
  }

  onClick() {
    if (!!this.widgetUpdateValueCallback) {
      this.widgetUpdateValueCallback.apply(this, [{ name: this.widgetConfigPayload?.name }]);
    }
  }
}

interface IButtonTypes {
  'primary': string;
  'secondary': string;
  'emphasis': string;
  'border': string;
  'quite': string;
  'split': string;
  'proceed': string;
  'accent': string;
  'success': string;
  'error': string;
  'warning': string;
  'quiteDefault': string;
}

export enum buttonTypes {
  'primary' = 'primary',
  'secondary' = 'secondary',
  'emphasis' = 'emphasis',
  'quite' = 'quite',
  'border' = 'border',
  'split' = 'split',
  'proceed' = 'proceed',
  'accent' = 'accent',
  'success' = 'success',
  'error' = 'error',
  'warning' = 'warning',
  'quiteDefault' = 'quiteDefault'
}
